/**
 * The half of a remembered browser's wrap key that comes from this machine.
 *
 * A passkey's PRF extension is a deterministic 32-byte answer to a salt, held
 * by the platform authenticator — Touch ID, Windows Hello — and released only
 * after the user verifies. That is the one thing browser storage cannot offer:
 * `extractable: false` is a JavaScript-level restriction, and the W3C spec says
 * outright that it "does not guarantee that the underlying cryptographic key
 * material will not be persisted to disk, possibly unencrypted". So a copied
 * profile defeats a non-extractable key and does not defeat this.
 *
 * Not a login. Nothing verifies the assertion — there is no server challenge to
 * check, because the answer being asked for is a key, not an identity. The
 * challenge is local and random for form's sake.
 *
 * Support is uneven enough that this is opportunistic: every function answers
 * null rather than throwing, and the caller falls back to a browser that is
 * remembered without it. The usual danger of PRF-derived keys — lose the
 * passkey, lose the data — does not apply here, because what it seals is a
 * convenience copy and the fallback is pairing with the phone again.
 */

import { bufferSource } from './bytes';

const RP_NAME = 'Pondering';
const CREDENTIAL_NAME = 'Pondering vault — this browser';

export interface PrfBinding {
  credentialId: Uint8Array;
  salt: Uint8Array;
}

/**
 * Three answers, not two. "This platform cannot" and "the person said no" both
 * produce no secret and must not be treated alike: the first is a machine to
 * fall back for, the second is an answer to honour.
 */
export type PrfEnrolment =
  | { status: 'enrolled'; binding: PrfBinding; secret: Uint8Array }
  | { status: 'unsupported' }
  | { status: 'declined' };

const available = (): boolean =>
  typeof PublicKeyCredential !== 'undefined' && !!navigator.credentials;

/**
 * Whether this machine offers a platform credential at all — a fingerprint, a
 * PIN, or a password manager standing in for one.
 *
 * Says nothing about PRF, and cannot: there is no way to ask whether an
 * authenticator does the extension without enrolling a credential, and no way
 * to delete one that turns out not to. That is exactly why the choice is
 * offered rather than taken on someone's behalf — a silent attempt leaves a
 * passkey behind that nothing will ever use.
 */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!available()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function firstResult(credential: PublicKeyCredential): Uint8Array | null {
  const results = credential.getClientExtensionResults() as {
    prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
  };
  const first = results.prf?.results?.first;
  return first ? new Uint8Array(first) : null;
}

function prfEnabled(credential: PublicKeyCredential): boolean {
  const results = credential.getClientExtensionResults() as { prf?: { enabled?: boolean } };
  return results.prf?.enabled === true;
}

/** Enrols a platform credential and derives this browser's half from it. */
export async function enrolPrf(accountLabel: string): Promise<PrfEnrolment> {
  if (!available()) return { status: 'unsupported' };
  const salt = crypto.getRandomValues(new Uint8Array(32));

  try {
    const credential = (await navigator.credentials.create({
      publicKey: {
        rp: { id: location.hostname, name: RP_NAME },
        user: {
          id: crypto.getRandomValues(new Uint8Array(16)),
          name: accountLabel || CREDENTIAL_NAME,
          displayName: CREDENTIAL_NAME,
        },
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        // Platform, verified: a roaming key would move the protection off this
        // machine, and hmac-secret needs user verification to answer at all.
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: 'required',
        },
        timeout: 120_000,
        extensions: { prf: { eval: { first: salt } } },
      },
    })) as PublicKeyCredential | null;
    if (!credential || !prfEnabled(credential)) return { status: 'unsupported' };

    const binding = { credentialId: new Uint8Array(credential.rawId), salt };
    // Some platforms evaluate during create and some only on an assertion, so
    // take the answer if it came and ask once more if it did not.
    const secret = firstResult(credential) ?? (await evaluatePrf(binding));
    return secret ? { status: 'enrolled', binding, secret } : { status: 'declined' };
  } catch (error) {
    // A refused prompt and a cancelled one both land here as NotAllowedError;
    // anything else is the platform saying it cannot, which is a different
    // answer and gets a different fallback.
    return (error as { name?: string })?.name === 'NotAllowedError'
      ? { status: 'declined' }
      : { status: 'unsupported' };
  }
}

/** The same 32 bytes as enrolment, after a user verification. Null when the
 *  user declines, the credential is gone, or the platform refuses. */
export async function evaluatePrf(binding: PrfBinding): Promise<Uint8Array | null> {
  if (!available()) return null;
  try {
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [{ type: 'public-key', id: bufferSource(binding.credentialId) }],
        userVerification: 'required',
        timeout: 120_000,
        extensions: { prf: { eval: { first: bufferSource(binding.salt) } } },
      },
    })) as PublicKeyCredential | null;
    return assertion ? firstResult(assertion) : null;
  } catch {
    return null;
  }
}
